import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { HighlightsService } from '@/services/highlights.service'

import {
	ICreateFolderParams,
	ISegment,
	IUpdateFolderMutationParams
} from '@/types/highlight.interface'
import { toastError } from '@/utils/toastError'

export const useHighlights = (taskId?: string) => {
	const queryClient = useQueryClient()

	const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null)

	const successFunction = () => {
		if (!taskId) return

		queryClient.invalidateQueries({
			queryKey: ['highlightsFolders', taskId]
		})
	}

	const { data: folders = [], isLoading: isFoldersLoading } = useQuery({
		queryKey: ['highlightsFolders', taskId],
		queryFn: () => HighlightsService.getFoldersList(taskId!),
		enabled: !!taskId,
		select: data => data.highlights
	})

	const { data: singleFolder, isLoading: isSingleFolderLoading } = useQuery({
		queryKey: ['highlightsFolder', selectedFolderId],
		queryFn: () => HighlightsService.getFolder(selectedFolderId!),
		enabled: !!selectedFolderId
	})

	const { mutateAsync: createHighlightFolder, isLoading: isFolderCreating } =
		useMutation({
			mutationKey: ['createHighlightFolder'],
			mutationFn: ({
				name,
				taskId,
				colorId,
				description,
				segments
			}: ICreateFolderParams) =>
				HighlightsService.createFolder({
					name,
					taskId,
					colorId,
					description,
					segments
				}),
			onSuccess: successFunction,
			onError: () => {
				toastError('Failed to create folder. Please try again.')
			}
		})

	const { mutateAsync: updateHighlightFolder, isLoading: isFolderUpdating } =
		useMutation({
			mutationKey: ['updateHighlightFolder'],
			mutationFn: ({
				highlightId,
				name,
				colorId,
				description
			}: IUpdateFolderMutationParams) =>
				HighlightsService.updateFolder(highlightId, {
					name,
					colorId,
					description
				}),
			onSuccess: successFunction,
			onError: () => {
				toastError('Failed to update folder. Please try again.')
			}
		})

	const { mutateAsync: deleteFolder, isLoading: isDeleteFolderLoading } =
		useMutation({
			mutationKey: ['deleteHighlightsFolder'],
			mutationFn: (highlightId: number) =>
				HighlightsService.deleteFolder(highlightId),
			onSuccess: successFunction,
			onError: () => {
				toastError('Failed to delete folder. Please try again.')
			}
		})

	const { mutateAsync: addSegmentToFolder, isLoading: isHighlightAdding } =
		useMutation({
			mutationKey: ['addHighlightToFolder'],
			mutationFn: ({
				highlightId,
				segment
			}: {
				highlightId: number
				segment: ISegment
			}) => HighlightsService.addHighlight({ highlightId, segment }),
			onSuccess: successFunction,
			onError: () => {
				toastError('Failed to save highlight. Please try again.')
			}
		})

	const { mutateAsync: deleteHighlight, isLoading: isHighlightDeleting } =
		useMutation({
			mutationKey: ['deleteHighlight'],
			mutationFn: ({
				highlightId,
				index
			}: {
				highlightId: number
				index: number
			}) => HighlightsService.deleteHighlight({ highlightId, index }),
			onSuccess: successFunction,
			onError: () => {
				toastError('Failed to delete highlight. Please try again.')
			}
		})

	return {
		folders,
		isFoldersLoading,
		selectedFolderId,
		setSelectedFolderId,
		singleFolder,
		isSingleFolderLoading,
		createHighlightFolder,
		isFolderCreating,
		updateHighlightFolder,
		isFolderUpdating,
		deleteFolder,
		isDeleteFolderLoading,
		addSegmentToFolder,
		isHighlightAdding,
		deleteHighlight,
		isHighlightDeleting
	}
}
